import React, { useState, useRef, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useCredits } from '@/context/CreditsContext';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import { toast } from 'react-toastify';

export type ChatMessage = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	timestamp: Date;
	/** When set, this is an error message; show content and a Retry, Log in again, or Go to Pricing action */
	errorKind?: 'forbidden' | 'retry' | 'insufficient_credits';
	/** For retry: the question that failed so we can resend */
	failedQuestion?: string;
};

export type AIChatOverlayProps = {
	/** Title shown in the overlay header (e.g. "AI Assistant") */
	title: string;
	/** Optional subtitle (e.g. batch name, recipe name) */
	subtitle?: string;
	/** Callback to send a question and get the AI answer. Parent provides context (batch, recipe, etc.) via closure. */
	onAsk: (question: string) => Promise<string>;
	/** Optional: load conversation history (e.g. by batchId/recipeId). Called once when overlay mounts or when historyKey changes. */
	loadHistory?: () => Promise<ChatMessage[]>;
	/** Stable key (e.g. batchId) so we only refetch history when it changes. Prevents refetch from overwriting new messages after send. */
	historyKey?: string;
	/** Optional suggested questions (templates) shown when chat is empty */
	suggestedQuestions?: string[];
	/** When true, show suggested question chips when chat is empty. Default from AI_CHAT_SHOW_SUGGESTIONS. */
	showSuggestions?: boolean;
	/** Optional placeholder for the input field */
	placeholder?: string;
	/** Optional welcome text when there are no messages */
	welcomeText?: string;
	/** Optional: render extra content in the header (e.g. CreditsDisplay) */
	headerExtra?: React.ReactNode;
	/** Position of the overlay (only used when variant is 'popup') */
	anchor?: 'bottom-right' | 'bottom-left';
	/** Initial expanded state */
	defaultExpanded?: boolean;
	/** Called when error is forbidden (401/403); e.g. () => navigate('/login') */
	onLoginAgain?: () => void;
	/** 'popup' = floating corner panel; 'footer' = full-width bar at bottom of page */
	variant?: 'popup' | 'footer';
};

/** Toggle here: true = full-width bottom footer always visible; false = floating popup */
export const AI_CHAT_USE_FOOTER = true;

/** Toggle here: true = show suggestion/template chips when chat is empty; false = hide them */
export const AI_CHAT_SHOW_SUGGESTIONS = true;

/** Height of the minimized footer bar (use for page padding when AI_CHAT_USE_FOOTER is true) */
export const AI_CHAT_FOOTER_BAR_HEIGHT = 64;

const OVERLAY_WIDTH = 380;
const OVERLAY_HEIGHT_EXPANDED = 520;
const MINIMIZED_BAR_HEIGHT = AI_CHAT_FOOTER_BAR_HEIGHT;
const MINIMIZED_BAR_WIDTH = 320;
const DEFAULT_VISIBLE_MESSAGES = 5;
const SHOW_OLDER_STEP = 5;

// localStorage keys
const STORAGE_KEY_FIRST_VISIT = 'ai-chat-first-visit';
const STORAGE_KEY_VARIANT_PREFERENCE = 'ai-chat-variant-preference';

function isForbiddenError(error: any): boolean {
	const msg = (error?.message || String(error)).toLowerCase();
	return (
		msg.includes('forbidden') ||
		msg.includes('401') ||
		msg.includes('403') ||
		msg.includes('unauthorized') ||
		msg.includes('authorization')
	);
}

const AIChatOverlay: React.FC<AIChatOverlayProps> = ({
	title,
	subtitle,
	onAsk,
	loadHistory,
	historyKey,
	suggestedQuestions = [],
	showSuggestions = AI_CHAT_SHOW_SUGGESTIONS,
	placeholder = 'Ask a question...',
	welcomeText = 'Ask me anything. I can help with your brewing questions.',
	headerExtra,
	anchor = 'bottom-right',
	defaultExpanded = false,
	onLoginAgain,
	variant: propVariant
}) => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // md = 960px
	const { totalCredits, setCreditsLoading, loadCredits } = useCredits();
	const hasNoCredits = totalCredits !== null && totalCredits < 1;
	const canSend = totalCredits !== null && totalCredits >= 1;
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [historyLoading, setHistoryLoading] = useState(!!loadHistory);
	const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_MESSAGES);
	const [suggestionsOpen, setSuggestionsOpen] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	
	// Determine variant: use prop if provided, otherwise check localStorage preference, otherwise use default
	const getInitialVariant = (): 'popup' | 'footer' => {
		if (propVariant) return propVariant;
		if (isDesktop) {
			const stored = localStorage.getItem(STORAGE_KEY_VARIANT_PREFERENCE);
			if (stored === 'popup' || stored === 'footer') return stored;
		}
		return AI_CHAT_USE_FOOTER ? 'footer' : 'popup';
	};
	
	const [variant, setVariant] = useState<'popup' | 'footer'>(getInitialVariant);
	
	// Sync variant with prop if provided
	useEffect(() => {
		if (propVariant) {
			setVariant(propVariant);
		}
	}, [propVariant]);
	
	// Check if this is first visit
	const [isFirstVisit, setIsFirstVisit] = useState(() => {
		return localStorage.getItem(STORAGE_KEY_FIRST_VISIT) !== 'true';
	});
	
	// Auto-expand on first visit or if defaultExpanded is true
	const [expanded, setExpanded] = useState(() => {
		if (defaultExpanded) return true;
		if (isFirstVisit) {
			localStorage.setItem(STORAGE_KEY_FIRST_VISIT, 'true');
			return true; // Auto-expand on first visit
		}
		return false;
	});

	// Show only the last N messages so the chat doesn't feel too long
	const displayedMessages = messages.length <= DEFAULT_VISIBLE_MESSAGES
		? messages
		: messages.slice(-Math.min(visibleCount, messages.length));
	const hasOlder = messages.length > DEFAULT_VISIBLE_MESSAGES && visibleCount < messages.length;
	const olderCount = messages.length - visibleCount;

	// Only load history when historyKey changes (e.g. batch id). Do NOT depend on loadHistory
	// or it would refetch on every parent render and overwrite new messages.
	useEffect(() => {
		if (!loadHistory || historyKey === undefined) {
			setHistoryLoading(false);
			return;
		}
		let cancelled = false;
		loadHistory()
			.then((loaded) => {
				if (!cancelled) {
					setMessages(loaded);
					setVisibleCount(DEFAULT_VISIBLE_MESSAGES);
				}
			})
			.catch(() => {
				if (!cancelled) setMessages([]);
			})
			.finally(() => {
				if (!cancelled) setHistoryLoading(false);
			});
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when entity changes
	}, [historyKey]);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [displayedMessages, expanded]);
	
	// Handle variant preference changes (only if propVariant is not provided)
	const handleVariantToggle = () => {
		if (propVariant) return; // Don't allow toggle if variant is controlled by prop
		const newVariant = variant === 'footer' ? 'popup' : 'footer';
		setVariant(newVariant);
		if (isDesktop) {
			localStorage.setItem(STORAGE_KEY_VARIANT_PREFERENCE, newVariant);
		}
	};
	
	// Clear first visit animation after a delay
	useEffect(() => {
		if (isFirstVisit && expanded) {
			const timer = setTimeout(() => {
				setIsFirstVisit(false);
			}, 3000); // Show animation for 3 seconds
			return () => clearTimeout(timer);
		}
	}, [isFirstVisit, expanded]);

	const handleSend = async (question?: string) => {
		const text = (question || input).trim();
		if (!text || loading) return;
		if (!canSend) {
			toast.error('You need more credits to use the AI. Get more on the Pricing page.');
			return;
		}

		const userMessage: ChatMessage = {
			id: `msg-${Date.now()}`,
			role: 'user',
			content: text,
			timestamp: new Date()
		};
		setMessages((prev) => [...prev, userMessage]);
		setInput('');
		setLoading(true);
		setCreditsLoading(true);

		try {
			const answer = await onAsk(text);
			const assistantMessage: ChatMessage = {
				id: `msg-${Date.now() + 1}`,
				role: 'assistant',
				content: answer,
				timestamp: new Date()
			};
			setMessages((prev) => [...prev, assistantMessage]);
		} catch (error: any) {
			console.error('Error getting AI response:', error);
			const rawMsg = error?.message || 'Failed to fetch';
			const insufficientCredits = /insufficient credit|no credit|not enough credit/i.test(rawMsg);
			if (insufficientCredits) {
				toast.error('You have no credits left. Buy more credits to continue.');
			}
			const forbidden = isForbiddenError(error);
			const errorContent = insufficientCredits
				? 'You have no credits left. Please buy more credits to continue using the AI.'
				: forbidden
				? 'Your session may have expired. Would you like to log in again?'
				: `Something went wrong: ${rawMsg}. Would you like to retry?`;
			const errorMessage: ChatMessage = {
				id: `msg-${Date.now() + 1}`,
				role: 'assistant',
				content: errorContent,
				timestamp: new Date(),
				errorKind: insufficientCredits ? 'insufficient_credits' : forbidden ? 'forbidden' : 'retry',
				failedQuestion: forbidden ? undefined : text
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setLoading(false);
			try {
				await loadCredits();
			} finally {
				setCreditsLoading(false);
			}
		}
	};

	const handleRetry = (failedQuestion: string) => {
		if (!canSend) {
			toast.error('You need more credits to use the AI. Get more on the Pricing page.');
			return;
		}
		setMessages((prev) => prev.slice(0, -1));
		setLoading(true);
		setCreditsLoading(true);
		onAsk(failedQuestion)
			.then((answer) => {
				setMessages((prev) => [
					...prev,
					{ id: `msg-${Date.now()}`, role: 'assistant' as const, content: answer, timestamp: new Date() }
				]);
			})
			.catch((error: any) => {
				const rawMsg = error?.message || 'Failed to fetch';
				const insufficientCredits = /insufficient credit|no credit|not enough credit/i.test(rawMsg);
				if (insufficientCredits) toast.error('You have no credits left. Buy more credits to continue.');
				const forbidden = isForbiddenError(error);
				const errorContent = insufficientCredits
					? 'You have no credits left. Please buy more credits to continue using the AI.'
					: forbidden
					? 'Your session may have expired. Would you like to log in again?'
					: `Something went wrong: ${rawMsg}. Would you like to retry?`;
				setMessages((prev) => [
					...prev,
					{
						id: `msg-${Date.now()}`,
						role: 'assistant',
						content: errorContent,
						timestamp: new Date(),
						errorKind: insufficientCredits ? 'insufficient_credits' : forbidden ? 'forbidden' : 'retry',
						failedQuestion: forbidden ? undefined : failedQuestion
					}
				]);
			})
			.finally(async () => {
				setLoading(false);
				try {
					await loadCredits();
				} finally {
					setCreditsLoading(false);
				}
			});
	};

	const anchorStyles = {
		'bottom-right': { right: 24, left: 'auto' },
		'bottom-left': { left: 24, right: 'auto' }
	}[anchor];

	const isFooter = variant === 'footer';

	return (
		<Box
			sx={{
				position: 'fixed',
				...(isFooter
					? { bottom: 0, left: 0, right: 0, zIndex: 1300, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }
					: { bottom: 24, ...anchorStyles, zIndex: 1300, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', pointerEvents: 'none' }
				)
			}}
		>
			<Paper
				elevation={8}
				sx={{
					pointerEvents: 'auto',
					width: isFooter ? '100%' : (expanded ? OVERLAY_WIDTH : MINIMIZED_BAR_WIDTH),
					maxWidth: isFooter ? '100%' : `calc(100vw - 32px)`,
					height: expanded ? OVERLAY_HEIGHT_EXPANDED : MINIMIZED_BAR_HEIGHT,
					borderRadius: isFooter ? (expanded ? '16px 16px 0 0' : '16px 16px 0 0') : 3,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					border: `1px solid ${theme.palette.divider}`,
					...(isFooter && { borderBottom: 'none' }),
					transition: 'height 0.25s ease, width 0.25s ease',
					...(isFirstVisit && expanded && {
						animation: 'slideUp 0.5s ease-out',
						'@keyframes slideUp': {
							from: {
								transform: 'translateY(20px)',
								opacity: 0.8
							},
							to: {
								transform: 'translateY(0)',
								opacity: 1
							}
						}
					})
				}}
			>
				{/* Minimized: single compact bar – click to expand */}
				{!expanded && (
					<Box
						role="button"
						tabIndex={0}
						onClick={() => setExpanded(true)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								setExpanded(true);
							}
						}}
						aria-label={`${title}. Click to open chat.`}
						sx={{
							height: MINIMIZED_BAR_HEIGHT,
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							px: 2,
							gap: 1.5,
							bgcolor: 'primary.main',
							color: 'primary.contrastText',
							cursor: 'pointer',
							'&:hover': { bgcolor: 'primary.dark' },
							'&:focus': { outline: '2px solid', outlineOffset: 2, outlineColor: 'primary.contrastText' },
							...(isFirstVisit && {
								animation: 'pulse 2s ease-in-out 3',
								'@keyframes pulse': {
									'0%, 100%': {
										boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)'
									},
									'50%': {
										boxShadow: '0 0 0 8px rgba(25, 118, 210, 0)'
									}
								}
							})
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
							<PsychologyIcon sx={{ fontSize: 28, flexShrink: 0 }} />
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="subtitle1" fontWeight={700} noWrap>
									{title}
								</Typography>
								<Typography variant="caption" sx={{ opacity: 0.9 }} noWrap display="block">
									{subtitle || placeholder}
								</Typography>
							</Box>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
							{headerExtra}
						</Box>
						<ExpandLessIcon sx={{ flexShrink: 0, fontSize: 28 }} aria-hidden />
					</Box>
				)}

				{/* Expanded: header + full chat */}
				{expanded && (
					<>
						{/* Expanded header */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								px: 2,
								py: 1.25,
								minHeight: 56,
								bgcolor: 'primary.main',
								color: 'primary.contrastText',
								flexShrink: 0
							}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
								<PsychologyIcon sx={{ fontSize: 24 }} />
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="subtitle1" fontWeight={700} noWrap>
										{title}
									</Typography>
									{subtitle && (
										<Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
											{subtitle}
										</Typography>
									)}
								</Box>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
								{headerExtra}
								{/* Desktop variant toggle - only show on desktop and when variant is not controlled by prop */}
								{isDesktop && !propVariant && (
									<Tooltip title={variant === 'footer' ? 'Switch to popup mode' : 'Switch to footer mode'}>
										<IconButton
											size="small"
											sx={{ color: 'primary.contrastText' }}
											onClick={(e) => {
												e.stopPropagation();
												handleVariantToggle();
											}}
											aria-label={variant === 'footer' ? 'Switch to popup mode' : 'Switch to footer mode'}
										>
											{variant === 'footer' ? <ViewCompactIcon /> : <ViewSidebarIcon />}
										</IconButton>
									</Tooltip>
								)}
								<IconButton
									size="small"
									sx={{ color: 'primary.contrastText' }}
									onClick={() => setExpanded(false)}
									aria-label="Minimize chat"
								>
									<ExpandMoreIcon />
								</IconButton>
							</Box>
						</Box>
						<Box
							sx={{
								flex: 1,
								overflowY: 'auto',
								overflowX: 'hidden',
								p: 2,
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								bgcolor: 'background.default'
							}}
						>
							{historyLoading && (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
									<CircularProgress size={28} />
								</Box>
							)}
							{!historyLoading && hasNoCredits && (
								<Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
										You have no credits left. Buy more credits to continue using the AI.
									</Typography>
									<Link component={RouterLink} to="/pricing" underline="hover" color="primary" fontWeight={600}>
										Go to Pricing →
									</Link>
								</Box>
							)}
							{!historyLoading && !hasNoCredits && messages.length === 0 && (
								<Box sx={{ py: 2 }}>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
										{welcomeText}
									</Typography>
									{showSuggestions && suggestedQuestions.length > 0 && (
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
											{suggestedQuestions.map((q, i) => (
												<Chip
													key={i}
													label={q}
													onClick={() => handleSend(q)}
													sx={{
														cursor: 'pointer',
														'&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' }
													}}
													variant="outlined"
													size="small"
												/>
											))}
										</Box>
									)}
								</Box>
							)}
							{hasOlder && (
								<Button
									size="small"
									variant="outlined"
									onClick={() => setVisibleCount((c) => Math.min(messages.length, c + SHOW_OLDER_STEP))}
									sx={{ alignSelf: 'center', mb: 0.5 }}
								>
									Show {Math.min(SHOW_OLDER_STEP, olderCount)} older message{olderCount !== 1 ? 's' : ''}
								</Button>
							)}
							{displayedMessages.map((message) => (
								<Box
									key={message.id}
									sx={{
										display: 'flex',
										gap: 1,
										justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{message.role === 'assistant' && (
										<Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
											<PsychologyIcon sx={{ fontSize: 18 }} />
										</Avatar>
									)}
									<Paper
										variant="outlined"
										sx={{
											p: 1.5,
											maxWidth: '85%',
											bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
											color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
											...(message.errorKind && {
												borderColor: 'error.main',
												borderWidth: 1,
												borderStyle: 'solid',
												bgcolor: 'error.light',
												color: 'error.dark'
											})
										}}
									>
										<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
											{message.content}
										</Typography>
										{message.role === 'assistant' && message.errorKind && (
											<Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
												{message.errorKind === 'forbidden' && onLoginAgain && (
													<Button size="small" variant="contained" onClick={onLoginAgain}>
														Log in again
													</Button>
												)}
												{message.errorKind === 'retry' && message.failedQuestion && (
													<Button size="small" variant="contained" onClick={() => handleRetry(message.failedQuestion!)}>
														Retry
													</Button>
												)}
												{message.errorKind === 'insufficient_credits' && (
													<Button size="small" variant="contained" component={RouterLink} to="/pricing">
														Go to Pricing
													</Button>
												)}
											</Stack>
										)}
									</Paper>
									{message.role === 'user' && (
										<Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>
											<Typography variant="caption" fontWeight={600}>U</Typography>
										</Avatar>
									)}
								</Box>
							))}
							{loading && (
								<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
									<Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
										<PsychologyIcon sx={{ fontSize: 18 }} />
									</Avatar>
									<Paper variant="outlined" sx={{ p: 1.5 }}>
										<CircularProgress size={18} />
									</Paper>
								</Box>
							)}
							<div ref={messagesEndRef} />
						</Box>
						<Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
							{showSuggestions && suggestedQuestions.length > 0 && (
								<Box sx={{ px: 1.5, pt: 1 }}>
									<Button
										size="small"
										startIcon={<LightbulbOutlinedIcon />}
										onClick={() => setSuggestionsOpen((o) => !o)}
										sx={{ textTransform: 'none', color: 'text.secondary' }}
										aria-expanded={suggestionsOpen}
										aria-label={suggestionsOpen ? 'Hide suggestions' : 'Show suggestions'}
									>
										{suggestionsOpen ? 'Hide suggestions' : 'Suggestions'}
									</Button>
									{suggestionsOpen && (
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pb: 1.5 }}>
											{suggestedQuestions.map((q, i) => (
												<Chip
													key={i}
													label={q}
													onClick={() => {
														handleSend(q);
														setSuggestionsOpen(false);
													}}
													disabled={loading || hasNoCredits}
													sx={{
														cursor: 'pointer',
														'&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' }
													}}
													variant="outlined"
													size="small"
												/>
											))}
										</Box>
									)}
								</Box>
							)}
							<Box sx={{ p: 1.5 }}>
							{hasNoCredits && (
								<Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
										You have no credits left. Buy more to continue.
									</Typography>
									<Link component={RouterLink} to="/pricing" underline="hover" color="primary" fontWeight={600}>
										Go to Pricing →
									</Link>
								</Box>
							)}
							<Stack direction="row" spacing={1}>
								<TextField
									fullWidth
									placeholder={hasNoCredits ? 'Buy more credits to ask questions' : placeholder}
									value={input}
									onChange={(e) => setInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey) {
											e.preventDefault();
											handleSend();
										}
									}}
									disabled={loading || hasNoCredits}
									multiline
									maxRows={3}
									size="small"
									sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
								/>
								<IconButton
									color="primary"
									onClick={() => handleSend()}
									disabled={loading || !input.trim() || hasNoCredits}
									aria-label="Send"
									sx={{ alignSelf: 'flex-end' }}
								>
									{loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
								</IconButton>
							</Stack>
							</Box>
						</Box>
					</>
				)}
			</Paper>
		</Box>
	);
};

export default AIChatOverlay;
